import { ReactElement, useMemo, useRef, useState } from 'react'
import { get2DLineOrthoDirection, projectWall2D } from '@editorUtils'
import { wallIsCartesian } from '@geometry'
import {
  DrawControllerWrapper,
  hashObject,
  toVector2,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import produce from 'immer'
import { filter, find, isNull, maxBy, reduce } from 'lodash-es'
import { Group, Line3 } from 'three'
import { useTheme } from '@mui/material'
import { useModelSnapTargets } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks'
import {
  InteractiveLine,
  InteractiveLineHandles,
  InteractiveLineRef,
  sceneConfig,
  TransientDrawState,
  getCenterMultiple,
  isPointOnLineSegment,
  toImmutable,
  ImmutableVector3,
  useCanvasListener,
} from '@modugen/ui-toolkit'
import { Line, Torus } from '@react-three/drei'
import { useControlStore, useControlStoreApi, useModelStore, useSceneStore } from '@editorStores'
import { useBlockAction, useClickActionEnabled, usePositionGuidToWallGuid } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { roundXYImmutableVector3 } from 'src/geometry/rounding'
import { NUM_DECIMALS_ROUND_WALL_EDIT, WALL_EDIT_IN_PROGRESS_BLOCK } from './constants'
import {
  applyIntelligentTranslation,
  IntelligentConnectedWall,
  findIntelligentConnectedWalls,
  getIntelligentPreviewLine,
  snapshotWalls,
} from './intelligentWallEdit'
import { translateOpening } from './utils'

const openingElevation = 0.1
const wallEndToOpeningThreshold = 0.1

const interactiveLineName = 'interactive-line'

interface Props {
  wallGuid: string
  onEditStart: () => void
  onEditStop: () => void
  reset: () => void
}

const WallEdit = ({ wallGuid, onEditStart, onEditStop }: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const lineRef = useRef<InteractiveLineRef>(null)
  const openingsGroupRef = useRef<Group>(null)
  const intelligentConnectedWallsRef = useRef<IntelligentConnectedWall[]>([])
  const intelligentOriginalStartRef = useRef<ImmutableVector3 | null>(null)

  const { walls, rips } = useModelStore(state => state.model)
  const updateWall = useModelStore(state => state.updateWall)
  const guidToColor = useSceneStore(state => state.guidToColor)

  const wall = useMemo(() => find(walls, ['guid', wallGuid]), [walls, wallGuid]) as ShapeObject

  const projectedWall = useMemo(() => projectWall2D(wall), [wall])

  const [isMoving, setIsMoving] = useState(false)
  const [activeHandle, setActiveHandle] = useState<
    typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles] | null
  >(null)
  const [intelligentPreviewLines, setIntelligentPreviewLines] = useState<
    [ImmutableVector3, ImmutableVector3][]
  >([])

  // Register a block while a move/extend is actively in progress (handle grabbed or moving). The
  // block outlives the interaction just long enough: the click that finalizes the edit resolves
  // before the `mouseup` that clears this state, so subscribers still see it and refuse to select.
  useBlockAction(
    WALL_EDIT_IN_PROGRESS_BLOCK,
    isMoving || activeHandle !== null ? 'provider' : undefined,
  )

  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const intelligentWallEditEnabled = useControlStore(state => state.intelligentWallEditEnabled)
  const setIntelligentDependentWallOriginals = useControlStore(
    state => state.setIntelligentDependentWallOriginals,
  )
  const controlStoreApi = useControlStoreApi()

  const positionGuidToWallGuid = usePositionGuidToWallGuid()

  const ripGuids = useMemo(() => rips.map(rip => rip.position_guid), [rips])

  const guidsToRejectAsSnapTargets = useMemo(() => {
    const filteredRips = filter(ripGuids, guid => positionGuidToWallGuid[guid] === wallGuid)

    return [wallGuid, ...filteredRips]
  }, [wallGuid, ripGuids, positionGuidToWallGuid])

  const modelSnapTargets = useModelSnapTargets({
    xyOnly: true,
    rejectedGuids: guidsToRejectAsSnapTargets,
  })
  const tapelineSnapTargets = useTapelineSnapTargets(true)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(true)
  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets],
  )

  // Effects
  useDefault2DSnapConfigForElementType(SnapTargetElementType.WALLS)

  // HOTKEYS

  const clearIntelligentPreview = () => {
    intelligentConnectedWallsRef.current = []
    intelligentOriginalStartRef.current = null
    setIntelligentPreviewLines([])
  }

  const updateIntelligentPreviewFromDelta = (delta: ImmutableVector3) => {
    const lines = intelligentConnectedWallsRef.current
      .map(connected => getIntelligentPreviewLine(connected, delta))
      .filter((line): line is [ImmutableVector3, ImmutableVector3] => line !== null)
    setIntelligentPreviewLines(lines)
  }

  useStructuralPlanningDrawerEsc(() => {
    onEditStop()
    if (activeHandle) {
      setActiveHandle(null)
      lineRef.current?.reset()
    } else if (isMoving) {
      setIsMoving(false)
      lineRef.current?.reset()
      clearIntelligentPreview()
    }
  }, !!activeHandle || isMoving)

  // MEMOS

  const wallCenter = useMemo(() => getCenterMultiple(projectedWall.points), [projectedWall])

  const wallDirection = useMemo(
    () => projectedWall.points[1].sub(projectedWall.points[0]).normalize(),
    [projectedWall],
  )

  const wallOrthoDirection = useMemo(() => {
    const direction2d = get2DLineOrthoDirection(
      toVector2(projectedWall.points[0]),
      toVector2(projectedWall.points[1]),
    )

    return new ImmutableVector3(direction2d.x, direction2d.y)
  }, [projectedWall])

  const drawingOrigin = useMemo(
    () =>
      activeHandle === InteractiveLineHandles.End
        ? projectedWall.points[0]
        : projectedWall.points[1],
    [activeHandle, projectedWall],
  )

  const projectedOpeningPoints = useMemo(
    () =>
      reduce(
        projectedWall.openings,
        (collector, opening) => [...collector, ...opening.points],
        [] as ImmutableVector3[],
      ),
    [projectedWall],
  )

  // EVENTS

  const moveStart = () => {
    if (!isMoving) {
      setIsMoving(true)
      onEditStart()

      if (intelligentWallEditEnabled && wallIsCartesian(wall)) {
        intelligentConnectedWallsRef.current = findIntelligentConnectedWalls(wall, walls)
        intelligentOriginalStartRef.current = projectedWall.points[0]
      } else {
        clearIntelligentPreview()
      }
    }
  }

  const editStop = (type: 'translation' | 'length') => {
    const wallLine = lineRef.current?.transientLine.current

    if (wallLine) {
      const [drawerStart, drawerEnd] = wallLine
      const start = roundXYImmutableVector3(drawerStart, NUM_DECIMALS_ROUND_WALL_EDIT)
      const end = roundXYImmutableVector3(drawerEnd, NUM_DECIMALS_ROUND_WALL_EDIT)
      const last = wall.shape.points.length - 1

      const originalStartV = wall.shape.points[0]
      const newStartV = start

      const newWall = produce(wall => {
        wall.shape.points[0] = new ImmutableVector3(start.x, start.y, wall.shape.points[0].z)
        wall.shape.points[1] = new ImmutableVector3(end.x, end.y, wall.shape.points[1].z)
        wall.shape.points[2] = new ImmutableVector3(end.x, end.y, wall.shape.points[2].z)
        wall.shape.points[last] = new ImmutableVector3(start.x, start.y, wall.shape.points[last].z)

        if (type === 'translation') {
          const movedDirection = newStartV.sub(originalStartV).normalize()
          const movedDistance = newStartV.distanceTo(originalStartV)

          wall.openings = wall.openings?.map(opening =>
            translateOpening(opening, movedDirection, movedDistance),
          )

          if (openingsGroupRef.current) {
            openingsGroupRef.current.position.set(0, 0, openingElevation)
          }

          wall.is_translated = true
        } else if (type === 'length') {
          wall.is_updated = activeHandle || undefined
        }
      }, wall)()
      updateWall(newWall)

      if (type === 'translation' && intelligentConnectedWallsRef.current.length > 0) {
        const delta = new ImmutableVector3(
          start.x - originalStartV.x,
          start.y - originalStartV.y,
          0,
        )
        const originals = snapshotWalls(
          intelligentConnectedWallsRef.current.map(({ wall }) => wall),
        )
        const updatedDependents = intelligentConnectedWallsRef.current
          .map(connected => applyIntelligentTranslation(connected, delta))
          .filter((updated): updated is ShapeObject => updated !== null)

        updatedDependents.forEach(updateWall)

        // Keep the earliest snapshot per wall so reset restores pre-intelligent-move geometry.
        const existingOriginals = controlStoreApi.getState().intelligentDependentWallOriginals
        const existingGuids = new Set(existingOriginals.map((wall: ShapeObject) => wall.guid))
        const newOriginals = originals.filter(
          original =>
            updatedDependents.some(updated => updated.guid === original.guid) &&
            !existingGuids.has(original.guid),
        )
        if (newOriginals.length > 0) {
          setIntelligentDependentWallOriginals([...existingOriginals, ...newOriginals])
        }
      }

      clearIntelligentPreview()
    }
  }

  const stopEditing = () => {
    editStop('length')
    setActiveHandle(null)
    onEditStop()
  }

  const stopMoving = () => {
    editStop('translation')
    setIsMoving(false)
    onEditStop()
  }

  useCanvasListener(
    'mouseup',
    event => {
      const isInput = event.target === lineRef.current?.input.current

      if (isMoving) {
        stopMoving()
      }
      if (!isInput && !lineRef.current?.inputActive.current && activeHandle) {
        stopEditing()
      }
    },
    [isMoving, activeHandle, editStop, stopEditing, stopMoving],
  )

  const moveLine = (transientDrawState: TransientDrawState) => {
    const wallLine = lineRef.current?.transientLine.current

    if (transientDrawState.drawPoint && wallLine) {
      const [start, end] = wallLine
      const drawStart2 = getCenterMultiple([start, end])

      const projectedStart = drawStart2.projectOnVector(wallOrthoDirection)
      const projectedEnd = transientDrawState.drawPoint.projectOnVector(wallOrthoDirection)
      const distance = projectedStart.distanceTo(projectedEnd)

      const drawDirection = transientDrawState.drawPoint.sub(drawStart2).normalize()

      lineRef.current?.moveLine(drawDirection, distance)

      if (openingsGroupRef.current) {
        const newPosition = openingsGroupRef.current.position.addScaledVector(
          drawDirection.v,
          distance,
        )
        openingsGroupRef.current?.position.set(newPosition.x, newPosition.y, openingElevation)
      }

      const updatedLine = lineRef.current?.transientLine.current
      const originalStart = intelligentOriginalStartRef.current
      if (originalStart && updatedLine) {
        const [newStart] = updatedLine
        updateIntelligentPreviewFromDelta(
          new ImmutableVector3(newStart.x - originalStart.x, newStart.y - originalStart.y, 0),
        )
      }
    }
  }

  const findDrawPoint = (
    drawingOrigin: ImmutableVector3,
    drawPoint: ImmutableVector3,
    openingPoints: ImmutableVector3[],
  ) => {
    // find all opening points that are not on the line drawn by the user. We
    // want to prevent the user from clipping an opening so he/she should only
    // be able to draw until he encounters an opening
    const openingPointsNotOnLine = filter(
      openingPoints,
      point =>
        !isPointOnLineSegment(
          new Line3(drawingOrigin.v, drawPoint.v),
          point.v,
          wallEndToOpeningThreshold,
        ),
    )

    let finalDrawPoint = drawPoint
    if (openingPointsNotOnLine.length) {
      drawPoint = maxBy(openingPointsNotOnLine, a =>
        a.distanceTo(drawingOrigin),
      ) as ImmutableVector3
    }

    // when the drawpoint is one of the projected opening points we want to set a small
    // threshold so the user is not removing one side of the opening and
    // therefore open the opening (pun intended)
    const openingPointWithingThresholdDistance = find(
      projectedOpeningPoints,
      point => point.distanceTo(drawPoint) < wallEndToOpeningThreshold,
    )
    if (openingPointWithingThresholdDistance) {
      const drawingDirection =
        activeHandle === InteractiveLineHandles.End ? wallDirection.v : wallDirection.v.negate()

      finalDrawPoint = openingPointWithingThresholdDistance.addScaledVector(
        toImmutable(drawingDirection),
        wallEndToOpeningThreshold,
      )
    }

    return finalDrawPoint
  }

  const extendLine = (transientDrawState: TransientDrawState) => {
    if (lineRef.current?.inputActive.current) return

    if (activeHandle && transientDrawState.drawPoint) {
      const drawingEnd = transientDrawState.drawPoint

      const drawPoint = findDrawPoint(drawingOrigin, drawingEnd, projectedOpeningPoints)

      lineRef.current?.updateHandle(activeHandle, drawPoint)
    }
  }

  const onExtendStart = (
    handle: typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles],
  ) => {
    if (isNull(activeHandle)) {
      onEditStart()
      setActiveHandle(handle)
    }
  }

  // Generate a hash for the wall object

  const zTranslation = 80

  const drawControllerEnabled = useClickActionEnabled()

  return (
    <>
      {/* draw controller for moving */}
      <DrawControllerWrapper
        drawingAxis={{
          origin: wallCenter,
          direction: wallOrthoDirection,
        }}
        indicatorType="crosshair"
        enableIndicator={false}
        enabled={isMoving && !activeHandle && drawControllerEnabled}
        onMouseMove={moveLine}
        additionalSnapTargets={additionalSnapTargets}
        snapToAngles={snapToAngles}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        xyOnly
        isValidDrawTarget={object => object.name !== interactiveLineName}
      />

      {/* draw controller for extending */}
      <DrawControllerWrapper
        drawingAxis={{
          origin: wallCenter,
          direction: wallDirection,
        }}
        indicatorType="crosshair"
        enableIndicator={false}
        enabled={!!activeHandle && !isMoving && drawControllerEnabled}
        onMouseMove={extendLine}
        additionalSnapTargets={additionalSnapTargets}
        snapToAngles={snapToAngles}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        xyOnly
      />

      <group position={[0, 0, zTranslation]}>
        <InteractiveLine
          key={hashObject(wall)}
          mainLineProps={{ name: interactiveLineName }}
          boundingLineProps={{ name: interactiveLineName }}
          ref={lineRef}
          line={projectedWall.points as [ImmutableVector3, ImmutableVector3]}
          onClick={moveStart}
          clickDisabled={!!activeHandle || !!wall.is_updated}
          showHandles={!isMoving && !wall.is_local && !wall.is_translated}
          onClickHandle={onExtendStart}
          clickHandleDisabled={isMoving || wall.is_translated}
          clickableHandles={
            wall.is_updated
              ? [
                  wall.is_updated as typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles],
                ]
              : activeHandle
              ? [activeHandle]
              : undefined
          }
          nonSelectable={isMoving || !!activeHandle}
          cursor={wall.is_updated || wall.is_local ? 'auto' : 'grab'}
          indicatorCursor={wall.is_translated || wall.is_local ? 'auto' : 'grab'}
          input={undefined}
          handleColor={activeHandle ? 'purple' : undefined}
        />

        {/* Red torus at projectedWall.points[0], indicating start */}
        <Torus
          position={[
            projectedWall.points[0].x,
            projectedWall.points[0].y,
            projectedWall.points[0].z,
          ]}
          args={[0.15, 0.01, 8, 16]}
        >
          <meshBasicMaterial color="red" />
        </Torus>
        {/* Black torus at projectedWall.points[0], indicating start */}
        <Torus
          position={[
            projectedWall.points[1].x,
            projectedWall.points[1].y,
            projectedWall.points[1].z,
          ]}
          args={[0.15, 0.01, 8, 16]}
        >
          <meshBasicMaterial color="black" />
        </Torus>

        <group
          ref={openingsGroupRef}
          position={[0, 0, openingElevation]}
          layers={sceneConfig.R3FNonSelectableObjectLayer}
        >
          {projectedWall.openings?.map((opening, i) => (
            <Line
              key={opening.guid || `${projectedWall.guid}-opening-${i}`}
              points={opening.points.map(p => p.v)}
              color={(guidToColor[opening.guid] || scenePalette.elements2d.openings) as string}
            />
          ))}
        </group>

        {intelligentPreviewLines.length > 0 && (
          <group layers={sceneConfig.R3FNonSelectableObjectLayer}>
            {intelligentPreviewLines.map((line, index) => (
              <Line
                key={`intelligent-preview-${index}`}
                points={[line[0].v, line[1].v]}
                color="purple"
                lineWidth={2}
              />
            ))}
          </group>
        )}
      </group>
    </>
  )
}

export default WallEdit
